import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { StickyContainer } from '@sparefoot/react-core';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { Button } from 'components/core/Button';
import { SearchBox } from 'components/search/SearchBox';
import { Filter } from 'components/svg';
import { ClickToCall } from 'components/facility/ClickToCall';
import { phonePropType } from 'config/propTypes';
import { H1, Paragraph } from 'components/core/Typography';
import MapPin from 'components/svg/MapPin';
import buildHeadline from 'utils/search/buildHeadline';
import { getTollfreePhoneNumber } from 'store/modules/phone';
import { locateUser } from 'utils/GeoLocation';
import { INTERNAL_PAGE_TYPES } from 'config/constants';
import NearMeSubheader from '../NearMeSubheader';
import SearchFiltersTray from '../SearchFiltersTray';

import './NewSearchToolbar.scss'; // ensure casing matches actual filename on disk

export function NewSearchToolbar({
	headline,
	location,
	onSearch,
	onOpenFilters,
	filtersCount,
	className,
	phone,
	actions,
	sticky,
	hidden,
	withinCallCenterHours,
	pageType,
	loading,
	// Filter tray props (for desktop dropdown)
	filtersShowing,
	closeFilters,
	searchParams,
	onFilter,
	searchType,
	unitFilters,
	facetCounts,
	useNewSearch,
}) {
	const [geoSearchPending, setGeoSearchPending] = useState(false);
	const [geoError, setGeoError] = useState(false);
	// Track if loading started during this geo cycle - prevents useEffect from
	// clearing geoSearchPending before the search even begins
	const [searchStarted, setSearchStarted] = useState(false);
	// Track if user is updating an existing location (clicked "Update Location"
	// after already having coords). Used to preserve "location set" message.
	const [isUpdating, setIsUpdating] = useState(false);

	// Derive hasCoords from URL params — persists across navigation unlike local state
	const hasCoords = !!(searchParams?.latitude && searchParams?.longitude);

	const isNearMePage = pageType === INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE;

	// When loading becomes true during a geo cycle, mark that search has started
	React.useEffect(() => {
		if (geoSearchPending && loading) {
			setSearchStarted(true);
		}
	}, [geoSearchPending, loading]);

	// Clear geoSearchPending when search completes (loading goes false AFTER it was true)
	React.useEffect(() => {
		if (geoSearchPending && searchStarted && !loading) {
			setGeoSearchPending(false);
			setSearchStarted(false);
			setIsUpdating(false);
			// Trigger StickyContainer height recalculation after subheader text changes
			requestAnimationFrame(() => {
				window.dispatchEvent(new Event('resize'));
			});
		}
	}, [geoSearchPending, searchStarted, loading]);

	// Expose locating state to children - true while geolocation or subsequent
	// search is in progress. Prevents showing stale location data.
	const locating = geoSearchPending;

	const locateMe = useCallback(() => {
		if (geoSearchPending) return;
		setGeoSearchPending(true);
		setGeoError(false);
		// If coords already exist, user is updating their location
		setIsUpdating(hasCoords);

		locateUser({
			onSuccess: ({ latitude, longitude }) => {
				// For NMP pages, update query params on current URL to preserve
				// the NMP route and avoid redirect to CLP (SF-20847)
				// Clear location/city/state so gateway uses coordinates instead
				if (isNearMePage && onFilter) {
					onFilter({
						latitude,
						longitude,
						location: undefined,
						city: undefined,
						state: undefined,
					});
				} else {
					onSearch({
						locationParams: { latitude, longitude },
					});
				}
				// Don't clear geoSearchPending here - useEffect clears it when
				// loading becomes false (search completes)
			},
			onError: () => {
				setGeoError(true);
				setGeoSearchPending(false);
				// Trigger StickyContainer height recalculation after error message appears
				requestAnimationFrame(() => {
					window.dispatchEvent(new Event('resize'));
				});
			},
		});
	}, [geoSearchPending, onSearch, onFilter, isNearMePage, hasCoords]);

	// Used by SearchBox locationParams to pre-fill the input field
	const locationDetails =
		location && location.city && location.state
			? `${location.city}, ${location.state}`
			: '';

	const headingText =
		headline || buildHeadline(searchParams, locationDetails);

	const subheaderProps = {
		geoError,
		searchLocation: location,
		locateMe,
		locating,
		hasCoords,
		isUpdating,
	};

	return React.createElement(
		sticky ? StickyContainer : Fragment,
		sticky ? { collapse: hidden } : undefined,
		// Apply 'no-click-to-call' when outside call center hours to mirror footer flex sizing semantics.
		<div
			className={classnames(
				'new-search-toolbar',
				className,
				{ hidden },
				{ 'no-click-to-call': !withinCallCenterHours },
			)}
		>
			{isNearMePage ? (
				<div className="new-search-toolbar__header-container">
					<H1
						size="medium"
						color="white"
						weight="extraBold"
						className="new-search-toolbar__heading"
					>
						{headingText}
					</H1>
					<NearMeSubheader
						className="near-me-desktop-subheader"
						{...subheaderProps}
					/>
				</div>
			) : (
				<H1
					size="medium"
					color="white"
					weight="extraBold"
					className="new-search-toolbar__heading"
				>
					{headingText}
				</H1>
			)}

			{withinCallCenterHours && (
				<ClickToCall
					phone={phone}
					onFetchNumber={actions.getTollfreePhoneNumber}
					phoneIcon
					buttonText="Need Help ?"
				/>
			)}

			<div className="new-search-toolbar__filter-container">
				<Button
					type="button"
					className={classnames('new-search-toolbar__filter', {
						'is-open': filtersShowing,
					})}
					onClick={onOpenFilters}
					aria-label="Open filters"
					aria-expanded={filtersShowing}
				>
					<Filter />
					<Paragraph
						color="white"
						size="small"
						weight="medium"
						last
					>
						Filter
					</Paragraph>
					{filtersCount > 0 && (
						<span
							className="new-search-toolbar__filter-count"
							aria-label={`${filtersCount} filters selected`}
						>
							{filtersCount}
						</span>
					)}
				</Button>
				{/* Desktop filter dropdown - positioned relative to filter button container */}
				<SearchFiltersTray
					filtersShowing={filtersShowing}
					closeFilters={closeFilters}
					searchParams={searchParams}
					onFilter={onFilter}
					searchType={searchType}
					unitFilters={unitFilters}
					facetCounts={facetCounts}
					useNewSearch={useNewSearch}
					className="desktop-filter-tray"
					idPrefix="desktop-"
				/>
			</div>
			{/* Mobile filter tray rendered in NewSearchPage to escape sticky toolbar stacking context */}
			<div className="tool-bar-search-box">
				<SearchBox
					id="new-search-toolbar-box"
					onSearch={onSearch}
					buttonText="Find Storage"
					icon={<MapPin fill="gray" />}
					inputLabel="Enter City or ZIP"
					location={locationDetails}
					locationParams={
						locationDetails
							? { location: locationDetails }
							: undefined
					}
					hideAutocompleteOnScroll
					useBrowserLocation
				/>
			</div>
			{isNearMePage && (
				<NearMeSubheader
					className="near-me-mobile-subheader"
					{...subheaderProps}
				/>
			)}
		</div>,
	);
}

NewSearchToolbar.propTypes = {
	/** Dynamic headline sourced from contentTitle (site content API) via headlineSelector */
	headline: PropTypes.string,
	location: PropTypes.object,
	onSearch: PropTypes.func.isRequired,
	onOpenFilters: PropTypes.func.isRequired,
	filtersCount: PropTypes.number,
	className: PropTypes.string,
	phone: phonePropType,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}).isRequired,
	sticky: PropTypes.bool,
	hidden: PropTypes.bool,
	withinCallCenterHours: PropTypes.bool.isRequired,
	pageType: PropTypes.string,
	loading: PropTypes.bool,
	// Filter tray props
	filtersShowing: PropTypes.bool,
	closeFilters: PropTypes.func,
	searchParams: PropTypes.object,
	onFilter: PropTypes.func,
	searchType: PropTypes.string,
	unitFilters: PropTypes.object,
	facetCounts: PropTypes.object,
	useNewSearch: PropTypes.bool,
};

NewSearchToolbar.defaultProps = {
	filtersCount: 0,
	phone: null,
	sticky: false,
	hidden: false,
	loading: false,
};

export const newSearchToolbarSelector = (state) => ({
	phone: state.phone.headerPhone,
});

export default compose(
	connect(
		newSearchToolbarSelector,
		{ getTollfreePhoneNumber },
		mapDispatchToActions,
	),
)(NewSearchToolbar);
